import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import { modalState } from '../../state/modal.state';
import { colors } from '@/theme/colors';
import { Button } from '@/ui/button/button';
import { Inline } from '@/ui/line/line';
import { Portal } from '@/ui/portal/portal';
import { Z_INDEX_MODAL_OVERLAY } from '@/theme/z-index';
import { useAtom } from 'jotai';
import { useKeyPressListener } from '@/hooks/use-key-press-listener';

export const ConfirmExploreRoundTruncateModal = () => {
  const { t } = useTranslation('explore');
  const [modal, setModalState] = useAtom(modalState);

  const close = () => {
    setModalState(null);
  };

  useKeyPressListener({ onKeyPress: close, key: 'Escape' });

  if (!modal || modal.state !== 'confirmExploreRoundTruncate') return null;

  const warningText = t('truncateRoundWarning', {
    activeRoundNumber: modal.activeRoundNumber,
    latestRoundNumber: modal.latestRoundNumber,
    nextRoundNumber: modal.activeRoundNumber + 1,
  });

  return (
    <Portal>
      <Overlay onClick={close}>
        <Dialog
          role="dialog"
          aria-modal="true"
          onClick={event => event.stopPropagation()}
        >
          <CloseButton type="button" aria-label={t`cancel`} onClick={close}>
            ×
          </CloseButton>
          <Title>{t`truncateRoundHeader`}</Title>
          <Body>{t`truncateRoundBody`}</Body>
          <WarningStrip>{warningText}</WarningStrip>
          <Footer>
            <BrandMark>
              Savvy.<em>AI</em>
            </BrandMark>
            <Actions>
              <Button variant="secondary" onClick={close}>{t`cancel`}</Button>
              <Button
                onClick={() => {
                  modal.onConfirm();
                  close();
                }}
              >
                {t`truncateRoundConfirm`}
              </Button>
            </Actions>
          </Footer>
        </Dialog>
      </Overlay>
    </Portal>
  );
};

const Overlay = styled('div')`
  position: fixed;
  inset: 0;
  z-index: ${Z_INDEX_MODAL_OVERLAY};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: rgba(99, 104, 139, 0.62);
`;

const Dialog = styled('div')`
  position: relative;
  width: 100%;
  max-width: 468px;
  background: ${({ theme }) => theme.colors.basics.white};
  border-radius: 14px;
  padding: 26px 26px 0;
  box-shadow: 0 18px 50px rgba(29, 29, 29, 0.18);
`;

const CloseButton = styled('button')`
  position: absolute;
  top: 0;
  right: 0;
  border: none;
  background: none;
  font-size: 20px;
  line-height: 1;
  color: ${({ theme }) => theme.colors.gray.c11};
  cursor: pointer;
`;

const Title = styled('h3')`
  margin: 0 0 10px;
  padding-right: 26px;
  font-size: 19px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.basics.black};
`;

const Body = styled('p')`
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.gray.c12};
`;

const WarningStrip = styled('div')`
  margin: 14px 0 4px;
  padding: 10px 12px;
  background: #fff6ed;
  border-left: 2px solid #ffae63;
  border-radius: 0 7px 7px 0;
  font-size: 12px;
  line-height: 1.5;
  color: #8a5a22;
`;

const Footer = styled(Inline)`
  align-items: center;
  justify-content: space-between;
  margin-top: 22px;
  padding: 14px 0 18px;
  border-top: 1px solid ${({ theme }) => theme.colors.gray.c2};
`;

const BrandMark = styled('div')`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.basics.black};

  em {
    font-style: normal;
    font-size: 8px;
    color: ${colors.accent.pink.c3};
    vertical-align: super;
    margin-left: 2px;
  }
`;

const Actions = styled(Inline)`
  gap: 8px;
`;
